import React, { useState, useEffect, useCallback } from 'react';
import { accountingAPI, formatCurrency } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Search, Eye, Trash2, FileText, Download, Pencil } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import api from '../services/api';
import { formatDateDisplay } from '../lib/dateUtils';

const VOUCHER_COLORS = {
  sales: 'bg-emerald-50 text-emerald-700',
  purchase: 'bg-violet-50 text-violet-700',
  journal: 'bg-slate-100 text-slate-700',
  receipt: 'bg-emerald-50 text-emerald-700',
  payment: 'bg-rose-50 text-rose-700',
  contra: 'bg-blue-50 text-blue-700',
  credit_note: 'bg-cyan-50 text-cyan-700',
  debit_note: 'bg-orange-50 text-orange-700',
  expense: 'bg-amber-50 text-amber-700',
};

export default function JournalEntryList() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ voucher_type: '', date_from: '', date_to: '', search: '', page: 1 });
  const [viewEntry, setViewEntry] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await accountingAPI.listJournalEntries(params);
      setEntries(res.data.entries);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch { toast.error('Failed to load entries'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const handleView = async (id) => {
    try {
      const res = await accountingAPI.getJournalEntry(id);
      setViewEntry(res.data);
    } catch { toast.error('Failed to load entry'); }
  };

  const handleDelete = async () => {
    try {
      await accountingAPI.deleteJournalEntry(deleteDialog.id);
      toast.success('Entry deleted');
      setDeleteDialog({ open: false, id: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div data-testid="journal-entry-list" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">Journal Entries</h1>
          <p className="text-sm text-slate-500 mt-1">{total} vouchers | Double-entry transactions</p>
        </div>
        <Button onClick={() => navigate('/accounting/journal-entries/new')}
          className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-2" data-testid="je-new-btn">
          <Plus className="w-4 h-4" /> New Voucher
        </Button>
        <button onClick={async () => {
          try {
            const params = new URLSearchParams();
            if (filters.voucher_type) params.set('voucher_type', filters.voucher_type);
            if (filters.date_from) params.set('date_from', filters.date_from);
            if (filters.date_to) params.set('date_to', filters.date_to);
            const res = await api.get(`/accounting/journal-entries/export?${params}`, { responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a'); a.href = url; a.download = 'journal_entries.csv'; a.click();
            toast.success('Journal entries exported');
          } catch { toast.error('Export failed'); }
        }} className="btn-accent flex items-center gap-1.5 text-xs px-3" data-testid="je-export">
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className="flex flex-col sm:flex-row flex-wrap gap-3 bg-white border border-slate-200 rounded-md p-4">
        <select value={filters.voucher_type} onChange={(e) => setFilters(f => ({ ...f, voucher_type: e.target.value, page: 1 }))}
          className="form-input w-full sm:w-40" data-testid="je-type-filter">
          <option value="">All Types</option>
          <option value="sales">Sales</option>
          <option value="purchase">Purchase</option>
          <option value="journal">Journal</option>
          <option value="receipt">Receipt</option>
          <option value="payment">Payment</option>
          <option value="contra">Contra</option>
          <option value="credit_note">Credit Note</option>
          <option value="debit_note">Debit Note</option>
          <option value="expense">Expense</option>
        </select>
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" placeholder="Search voucher no. or narration..." value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))}
            className="form-input pl-9" data-testid="je-search" />
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <input type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value, page: 1 }))}
            className="form-input flex-1 sm:w-36" data-testid="je-date-from" />
          <input type="date" value={filters.date_to} onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value, page: 1 }))}
            className="form-input flex-1 sm:w-36" data-testid="je-date-to" />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full data-table" data-testid="je-table">
              <thead>
                <tr><th>S.No.</th><th>Date</th><th>Voucher No.</th><th>Type</th><th>Narration</th><th className="text-right">Debit</th><th className="text-right">Credit</th><th className="text-center">Actions</th></tr>
              </thead>
              <tbody>
                {entries.map((e, idx) => (
                  <tr key={e.entry_id} data-testid={`je-row-${e.entry_id}`}>
                    <td className="text-xs text-slate-400">{(filters.page - 1) * 20 + idx + 1}</td>
                    <td className="text-xs text-slate-500">{formatDateDisplay(e.date)}</td>
                    <td className="font-mono text-xs font-medium">{e.voucher_number}</td>
                    <td>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${VOUCHER_COLORS[e.voucher_type] || 'bg-slate-100'}`}>
                        {e.voucher_type}
                      </span>
                    </td>
                    <td className="text-sm text-slate-600 max-w-[200px] truncate">{e.narration || '-'}</td>
                    <td className="text-right font-mono text-sm">Rs. {formatCurrency(e.total_debit)}</td>
                    <td className="text-right font-mono text-sm">Rs. {formatCurrency(e.total_credit)}</td>
                    <td>
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => handleView(e.entry_id)} className="p-1.5 hover:bg-slate-100 rounded" title="View" data-testid={`je-view-${e.entry_id}`}>
                          <Eye className="w-3.5 h-3.5 text-slate-500" />
                        </button>
                        {!e.is_auto_posted && (
                          <button onClick={() => navigate(`/accounting/journal-entries/${e.entry_id}/edit`)} className="p-1.5 hover:bg-blue-50 rounded" title="Edit" data-testid={`je-edit-${e.entry_id}`}>
                            <Pencil className="w-3.5 h-3.5 text-blue-500" />
                          </button>
                        )}
                        <button onClick={() => setDeleteDialog({ open: true, id: e.entry_id })} className="p-1.5 hover:bg-rose-50 rounded" title="Delete" data-testid={`je-delete-${e.entry_id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {entries.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-slate-400 py-12">No journal entries found. Create your first voucher.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <p className="text-sm text-slate-500">Page {filters.page} of {pages} ({total} entries)</p>
            <div className="flex gap-2">
              <button onClick={() => setFilters(f => ({ ...f, page: Math.max(1, f.page - 1) }))} disabled={filters.page <= 1} className="btn-secondary px-3 py-1.5 text-xs">Prev</button>
              <button onClick={() => setFilters(f => ({ ...f, page: Math.min(pages, f.page + 1) }))} disabled={filters.page >= pages} className="btn-secondary px-3 py-1.5 text-xs">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* View Entry Dialog */}
      <Dialog open={!!viewEntry} onOpenChange={(o) => !o && setViewEntry(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Voucher: {viewEntry?.voucher_number}</DialogTitle></DialogHeader>
          {viewEntry && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div><span className="text-slate-500">Date:</span> <span className="font-medium">{formatDateDisplay(viewEntry.date)}</span></div>
                <div><span className="text-slate-500">Type:</span> <span className="font-medium capitalize">{viewEntry.voucher_type}</span></div>
                <div><span className="text-slate-500">Created by:</span> <span className="font-medium">{viewEntry.created_by_name}</span></div>
              </div>
              {viewEntry.narration && <p className="text-sm text-slate-600 bg-slate-50 rounded p-3">{viewEntry.narration}</p>}
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50"><th className="px-3 py-2 text-left">Account</th><th className="px-3 py-2 text-right">Debit</th><th className="px-3 py-2 text-right">Credit</th></tr></thead>
                <tbody>
                  {viewEntry.lines?.map((l, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="px-3 py-2"><span className="font-mono text-xs text-slate-500 mr-2">{l.account_code}</span>{l.account_name}</td>
                      <td className="px-3 py-2 text-right font-mono">{l.debit > 0 ? `Rs. ${formatCurrency(l.debit)}` : '-'}</td>
                      <td className="px-3 py-2 text-right font-mono">{l.credit > 0 ? `Rs. ${formatCurrency(l.credit)}` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold bg-slate-50">
                    <td className="px-3 py-2">Total</td>
                    <td className="px-3 py-2 text-right font-mono">Rs. {formatCurrency(viewEntry.total_debit)}</td>
                    <td className="px-3 py-2 text-right font-mono">Rs. {formatCurrency(viewEntry.total_credit)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewEntry(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(o) => !o && setDeleteDialog({ open: false, id: '' })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete Voucher</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Are you sure? This will reverse the accounting impact.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, id: '' })}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="je-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
